import React, { useState } from 'react';
import { FileText, Send, X, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { submitRFQ } from '../api/api';

const RequestQuote = () => {
  const { language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    country: '',
    portOfDischarge: '',
    productCategory: [],
    productDetails: '',
    quantity: '',
    estimatedQuantity: '',
    deliveryTimeline: '',
    additionalRequirements: ''
  });

  const content = {
    en: {
      title: 'Request a Quote',
      subtitle: 'Fill in the form below and our team will provide a competitive quotation',
      companyInfo: 'Company Information',
      requirements: 'Product Requirements',
      submit: 'Submit RFQ',
      companyName: 'Company Name',
      contactPerson: 'Contact Person',
      email: 'Email Address',
      phone: 'Phone Number',
      country: 'Country',
      portOfDischarge: 'Port of Discharge',
      productCategory: 'Product/Service Category',
      productDetails: 'Product Details / Specifications',
      quantity: 'Quantity Required',
      estimatedQuantity: 'Estimated Monthly/Annual Quantity',
      deliveryTimeline: 'Required Delivery Timeline',
      additionalRequirements: 'Additional Requirements',
      selectCategories: 'Select categories',
      success: 'RFQ submitted successfully! We will contact you within 24 hours.',
      error: 'Please fill in all required fields.'
    },
    vi: {
      title: 'Yêu cầu báo giá',
      subtitle: 'Điền vào biểu mẫu bên dưới và nhóm của chúng tôi sẽ cung cấp báo giá cạnh tranh',
      companyInfo: 'Thông tin công ty',
      requirements: 'Yêu cầu sản phẩm',
      submit: 'Gửi RFQ',
      companyName: 'Tên công ty',
      contactPerson: 'Người liên hệ',
      email: 'Địa chỉ email',
      phone: 'Số điện thoại',
      country: 'Quốc gia',
      portOfDischarge: 'Cảng dỡ hàng',
      productCategory: 'Danh mục sản phẩm/dịch vụ',
      productDetails: 'Chi tiết sản phẩm / Thông số',
      quantity: 'Số lượng yêu cầu',
      estimatedQuantity: 'Số lượng ước tính hàng tháng/năm',
      deliveryTimeline: 'Thời gian giao hàng yêu cầu',
      additionalRequirements: 'Yêu cầu bổ sung',
      selectCategories: 'Chọn danh mục',
      success: 'Gửi RFQ thành công! Chúng tôi sẽ liên hệ trong vòng 24 giờ.',
      error: 'Vui lòng điền tất cả các trường bắt buộc.'
    }
  };

  const productCategories = [
    { value: 'industrial-machinery', label: language === 'en' ? 'Industrial Machinery' : 'Máy móc công nghiệp' },
    { value: 'mep-equipment', label: language === 'en' ? 'MEP Equipment' : 'Thiết bị MEP' },
    { value: 'water-pumps', label: language === 'en' ? 'Water Pumps' : 'Máy bơm nước' },
    { value: 'pipe-fittings', label: language === 'en' ? 'Pipe Fittings & Valves' : 'Phụ kiện ống & van' },
    { value: 'pool-equipment', label: language === 'en' ? 'Pool Systems & Spares' : 'Hệ thống hồ bơi' },
    { value: 'chemicals', label: language === 'en' ? 'Water Treatment Chemicals' : 'Hóa chất xử lý nước' },
    { value: 'engineering-spares', label: language === 'en' ? 'Engineering Spares' : 'Phụ tùng kỹ thuật' },
    { value: 'cad-production', label: language === 'en' ? 'CAD Production' : 'Sản xuất CAD' },
    { value: 'mep-design', label: language === 'en' ? 'MEP Design Services' : 'Dịch vụ thiết kế MEP' },
    { value: 'other', label: language === 'en' ? 'Other' : 'Khác' }
  ];

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.contactPerson || !formData.email || !formData.phone || !formData.country || 
        formData.productCategory.length === 0 || !formData.productDetails || !formData.quantity || 
        !formData.estimatedQuantity || !formData.deliveryTimeline) {
      toast.error(t.error);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitRFQ(formData);
      toast.success(t.success);
      
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        country: '',
        portOfDischarge: '',
        productCategory: [],
        productDetails: '',
        quantity: '',
        estimatedQuantity: '',
        deliveryTimeline: '',
        additionalRequirements: ''
      });
    } catch (error) {
      console.error('RFQ submission error:', error);
      toast.error(language === 'en' ? 'Failed to submit RFQ. Please try again.' : 'Gửi RFQ thất bại. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const toggleCategory = (value) => {
    if (formData.productCategory.includes(value)) {
      setFormData({
        ...formData,
        productCategory: formData.productCategory.filter(cat => cat !== value)
      });
    } else {
      setFormData({
        ...formData,
        productCategory: [...formData.productCategory, value]
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-[#264f78] to-[#1a3a57] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <FileText className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-lg text-white/90">{t.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.companyInfo}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="companyName">{t.companyName} *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson">{t.contactPerson} *</Label>
                    <Input
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t.email} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t.phone} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">{t.country} *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="portOfDischarge">{t.portOfDischarge}</Label>
                    <Input
                      id="portOfDischarge"
                      name="portOfDischarge"
                      value={formData.portOfDischarge}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.requirements}</h2>
                <div className="space-y-6">
                  <div>
                    <Label>{t.productCategory} *</Label>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                      {productCategories.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => toggleCategory(cat.value)}
                          className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                            formData.productCategory.includes(cat.value)
                              ? 'bg-[#264f78] text-white border-[#264f78]'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-[#264f78]'
                          }`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="productDetails">{t.productDetails} *</Label>
                    <Textarea
                      id="productDetails"
                      name="productDetails"
                      value={formData.productDetails}
                      onChange={handleChange}
                      placeholder="Please provide detailed specifications: model, size, pressure rating, material, etc."
                      className="mt-1 min-h-[120px]"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="quantity">{t.quantity} *</Label>
                      <Input
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        placeholder=""
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="estimatedQuantity">{t.estimatedQuantity} *</Label>
                      <Input
                        id="estimatedQuantity"
                        name="estimatedQuantity"
                        value={formData.estimatedQuantity}
                        onChange={handleChange}
                        placeholder=""
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="deliveryTimeline">{t.deliveryTimeline} *</Label>
                    <Input
                      id="deliveryTimeline"
                      name="deliveryTimeline"
                      value={formData.deliveryTimeline}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="additionalRequirements">{t.additionalRequirements}</Label>
                    <Textarea
                      id="additionalRequirements"
                      name="additionalRequirements"
                      value={formData.additionalRequirements}
                      onChange={handleChange}
                      placeholder="Any additional requirements, certifications needed, payment terms, etc."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#264f78] hover:bg-[#1a3a57] text-white py-6 text-lg transition-all duration-300"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    {language === 'en' ? 'Submitting...' : 'Đang gửi...'}
                  </>
                ) : (
                  <>
                    {t.submit}
                    <Send className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RequestQuote;
